import {
  CreateOrderResponse,
  DataProps,
  DeliveryOrdersResponse,
  Order,
  OrderHistory,
  OrderHistoryParams,
  WaitingPaymentsSummary,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, isAxiosError } from 'axios';
import cleanDeep from 'clean-deep';

export class DeliveryOrdersRequestError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'DeliveryOrdersRequestError';
    this.statusCode = statusCode;
  }
}

const EMPTY_WAITING_PAYMENTS_SUMMARY: WaitingPaymentsSummary = {
  count: 0,
  single_order: null,
};

type WaitingPaymentsSummaryValidationReason =
  | 'response-not-object'
  | 'count-invalid'
  | 'single-order-not-null-for-zero-count'
  | 'single-order-not-null-for-multiple-count'
  | 'single-order-not-object-for-one-count'
  | 'single-order-fields-invalid';

const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getResponseBodyType = (value: unknown) => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const normalizeWaitingPaymentsSummary = (
  value: unknown
): {
  validationReason: WaitingPaymentsSummaryValidationReason | null;
  summary: WaitingPaymentsSummary;
} => {
  if (!isUnknownRecord(value)) {
    return {
      validationReason: 'response-not-object',
      summary: EMPTY_WAITING_PAYMENTS_SUMMARY,
    };
  }

  const count = value['count'];
  const singleOrder = value['single_order'];
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
    return {
      validationReason: 'count-invalid',
      summary: EMPTY_WAITING_PAYMENTS_SUMMARY,
    };
  }

  if (count === 0) {
    return {
      validationReason:
        singleOrder === null ? null : 'single-order-not-null-for-zero-count',
      summary: EMPTY_WAITING_PAYMENTS_SUMMARY,
    };
  }

  if (count >= 2) {
    return {
      validationReason:
        singleOrder === null
          ? null
          : 'single-order-not-null-for-multiple-count',
      summary: { count, single_order: null },
    };
  }

  if (!isUnknownRecord(singleOrder)) {
    return {
      validationReason: 'single-order-not-object-for-one-count',
      summary: { count, single_order: null },
    };
  }

  const orderMetaId = singleOrder['order_meta_id'];
  const paymentSubState = singleOrder['payment_sub_state'];
  if (
    typeof orderMetaId !== 'string' ||
    (paymentSubState !== 'instruction' && paymentSubState !== 'proof_draft')
  ) {
    return {
      validationReason: 'single-order-fields-invalid',
      summary: { count, single_order: null },
    };
  }

  return {
    validationReason: null,
    summary: {
      count,
      single_order: {
        order_meta_id: orderMetaId,
        payment_sub_state: paymentSubState,
      },
    },
  };
};

export const getOrderHistory = async (
  token: string,
  payload: OrderHistoryParams
) => {
  try {
    const { data } = await apiInstance(token).get<OrderHistory>(
      `/order/histories`,
      {
        params: cleanDeep(payload),
      }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(
        axiosError.response?.data as DataProps,
        (scope, user) => {
          scope.setTag('OrderHistory', `metaId ${payload['warehouse']}`);
          scope.setUser({
            ...(user ?? {}),
            additional: {
              warehouse: payload['warehouse'],
            },
          });
          scope.setLevel('error');
        }
      );
    }
  }
  throw new Error('Error Get OrderHistory');
};

export const getOrderDetail = async (
  token: string,
  metaId: string,
  warehouse: string
): Promise<Order> => {
  try {
    const { data } = await apiInstance(token).get<Order>(
      `/order/meta/${metaId}?warehouse=${warehouse}`
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(
        axiosError.response?.data as DataProps,
        (scope, user) => {
          scope.setTag('OrderDetail', `metaId ${metaId}`);
          scope.setUser({
            ...(user ?? {}),
            additional: {
              warehouse,
            },
          });
          scope.setLevel('error');
        }
      );
    }
  }
  throw new Error('Error Get Orderdetail');
};

export const getWaitingPaymentsSummary = async (
  token: string,
  warehouse: string
): Promise<WaitingPaymentsSummary> => {
  try {
    const response = await apiInstance(token).get<WaitingPaymentsSummary>(
      '/order/waiting-payments/summary',
      {
        params: { warehouse },
      }
    );

    if (response.status === 205) {
      return EMPTY_WAITING_PAYMENTS_SUMMARY;
    }

    const normalized = normalizeWaitingPaymentsSummary(response.data);
    if (normalized.validationReason !== null) {
      buyerCaptureException(
        {
          message: 'Malformed waiting payments summary',
          warehouse,
        },
        (scope) => {
          scope.setTag('feature', 'payment_instruction');
          scope.setTag('source', 'sticky');
          scope.setTag('payment_instruction', 'malformed-summary');
          scope.setContext('payment_instruction', {
            reason: 'malformed-summary',
            source: 'sticky',
            warehouse,
            httpStatus: response.status,
            bodyType: getResponseBodyType(response.data),
            validationReason: normalized.validationReason,
          });
          scope.setLevel('error');
        }
      );
    }

    return normalized.summary;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const status = error.response?.status;

      if (status === 422) {
        return EMPTY_WAITING_PAYMENTS_SUMMARY;
      }

      if (status === 401) {
        throw new Error('Error Get WaitingPaymentsSummary');
      }

      buyerCaptureException(
        {
          message: error.message,
          status_code: status ?? 0,
          warehouse,
        },
        (scope, user) => {
          scope.setTag('feature', 'payment_instruction');
          scope.setTag('source', 'sticky');
          scope.setTag('payment_instruction', 'summary-api-failed');
          scope.setContext('payment_instruction', {
            reason: 'summary-api-failed',
            source: 'sticky',
            warehouse,
          });
          scope.setUser({
            ...(user ?? {}),
            additional: { warehouse },
          });
          scope.setLevel('error');
        }
      );
    }

    throw new Error('Error Get WaitingPaymentsSummary');
  }
};

export const createOrder = async (
  token: string,
  payload: {
    warehouse_id: string;
    products: Array<{ id: string; quantity: number; note: string | null }>;
    voucher_codes?: string[];
  }
) => {
  try {
    const url = `/order`;
    const { data } = await apiInstance(token).post<CreateOrderResponse>(
      url,
      payload
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const errorData = error.response?.data as DataProps;
      buyerCaptureException(errorData, (scope) => {
        scope.setTags({
          queryKey: 'createOrder error',
          status_code: (errorData as { statusCode?: number })?.statusCode,
        });
        scope.setContext('error', {
          error: errorData,
        });
        scope.setExtra('warehouse_id', payload.warehouse_id);
        scope.setExtra('products_count', payload.products.length);
        scope.setLevel('error');
      });
      throw new Error(
        (errorData as { message?: string })?.message || 'Failed to create order'
      );
    }
    throw error;
  }
};

// ==================== Delivery Orders (Multiple DO Handling) ====================

/**
 * Get delivery orders for an order meta
 * API Endpoint: GET /api/v1/order/meta/{orderMetaId}/delivery-orders
 */
export const getDeliveryOrders = async (
  token: string,
  orderMetaId: string,
  warehouse: string
): Promise<DeliveryOrdersResponse> => {
  try {
    const { data } = await apiInstance(token).get<DeliveryOrdersResponse>(
      `/order/meta/${orderMetaId}/delivery-orders`,
      { params: { warehouse } }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as DataProps | undefined;
      buyerCaptureException(responseData, (scope, user) => {
        scope.setTag('DeliveryOrders', `orderMetaId ${orderMetaId}`);
        scope.setUser({
          ...(user ?? {}),
          additional: { orderMetaId, warehouse },
        });
        scope.setLevel('error');
      });
      throw new DeliveryOrdersRequestError(
        (responseData as { message?: string })?.message ??
          'Error Get DeliveryOrders',
        axiosError.response?.status
      );
    }
    throw new DeliveryOrdersRequestError('Error Get DeliveryOrders');
  }
};
